import { type FC, useEffect, useRef, useState } from 'react';
import styles from './TimepointEditor.module.scss';
import { _isEmpty, _isNotEmpty, _notNil } from '@/littledash';
import { RiCloseFill } from 'react-icons/ri';
import { errorToast } from '@/helpers';
import { parse as durationParse } from 'tinyduration';
import { type Duration } from 'date-fns';
import { DateUtils } from '@/utils/Date.utils';
import { createTimepointErrorMessage } from '@/components/UI/SpreadSheet/editors/TimepointErrorHandler';

export type TimepointAction =
  | { type: 'update'; value: Array<string>; close: boolean; keypress?: { key: 'Enter' | 'Tab'; shiftKey: boolean } }
  | { type: 'close'; revert: boolean };

interface TimepointComponentProps {
  timepoints: Array<string>;
  handleAction: (action: TimepointAction) => void;
  isSingleValueOnly?: boolean;
}

const toSeconds = (value: Duration & { negative?: boolean }): number => {
  return (
    ((value?.days ?? 0) * 86400 + (value?.hours ?? 0) * 3600 + (value?.minutes ?? 0) * 60 + (value?.seconds ?? 0)) *
    (value?.negative === true ? -1 : 1)
  );
};
export const timepointComparator = (left: string, right: string) =>
  toSeconds(durationParse(left)) - toSeconds(durationParse(right));

export const TimepointComponent: FC<TimepointComponentProps> = ({ timepoints, handleAction, isSingleValueOnly }) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [timepointValues, updateTimepointValues] = useState<Set<string>>(() => new Set(timepoints ?? []));

  useEffect(() => {
    updateTimepointValues(() => {
      return new Set((timepoints ?? []).sort(timepointComparator));
    });
  }, [timepoints]);

  useEffect(() => {
    if (_notNil(inputRef.current)) {
      const abortController = new AbortController();
      inputRef.current.focus();

      inputRef.current.addEventListener(
        'keydown',
        (event: KeyboardEvent) => {
          switch (event.key) {
            case 'Tab':
            case 'Enter': {
              event.preventDefault();
              event.stopPropagation();
              const value = (inputRef.current?.value?.split(',') ?? []).reduce<Array<string>>((acc, v) => {
                const result = v.trim();
                if (_isNotEmpty(result)) {
                  acc.push(result);
                }
                return acc;
              }, []);
              if (_isNotEmpty(value)) {
                Promise.allSettled(value.map((value) => DateUtils.parseDuration(value)) ?? [])
                  .then((results) => {
                    const valuesToAdd = results.reduce<Array<string>>((acc, result, index) => {
                      if (result.status === 'fulfilled') {
                        acc.push(result.value);
                      } else {
                        const originalValues = value.reduce<Array<string>>((acc, v) => {
                          const trimmed = v.trim();
                          if (_isNotEmpty(trimmed)) {
                            acc.push(trimmed);
                          }
                          return acc;
                        }, []);
                        const failedValue = originalValues[index] ?? inputRef.current?.value ?? '';
                        errorToast(
                          createTimepointErrorMessage('error_format', {
                            values: [],
                            originalInput: failedValue,
                          })
                        );
                      }
                      return acc;
                    }, []);
                    if (_isNotEmpty(valuesToAdd)) {
                      updateTimepointValues((prevState) => {
                        let update: Array<string>;
                        if (isSingleValueOnly === true) {
                          // Single-value mode: replace existing value
                          update = valuesToAdd.slice(0, 1);
                        } else {
                          // Multi-value mode: add to existing
                          update = [...Array.from(prevState), ...valuesToAdd].sort(timepointComparator);
                        }
                        handleAction({
                          type: 'update',
                          value: update,
                          close: true,
                          keypress: {
                            key: event.key as 'Enter' | 'Tab',
                            shiftKey: event.shiftKey,
                          },
                        });
                        return new Set(update);
                      });
                    }
                  })
                  .catch(() => {
                    errorToast(
                      createTimepointErrorMessage('error_format', {
                        originalInput: inputRef.current?.value ?? '',
                      })
                    );
                  })
                  .finally(() => {
                    if (_notNil(inputRef.current)) {
                      inputRef.current.value = '';
                    }
                  });
              } else {
                handleAction({ type: 'close', revert: false });
              }
              break;
            }
            case 'Backspace': {
              if (_notNil(inputRef.current)) {
                event.stopPropagation();
                if (_isEmpty(inputRef.current.value)) {
                  event.preventDefault();
                  event.stopPropagation();
                  updateTimepointValues((prevState) => {
                    const values = Array.from(prevState);
                    values.pop();
                    handleAction({ type: 'update', value: values, close: prevState.size === 0 });
                    return new Set(values);
                  });
                }
              }
              break;
            }
            case 'Escape': {
              if (_notNil(inputRef.current)) {
                event.preventDefault();
                event.stopPropagation();
                if (_isNotEmpty(inputRef.current.value)) {
                  inputRef.current.value = '';
                } else {
                  handleAction({ type: 'close', revert: false });
                }
              }
              break;
            }
          }
        },
        {
          passive: false,
          signal: abortController.signal,
        }
      );
      return () => {
        abortController.abort();
      };
    }
  }, [inputRef, isSingleValueOnly]);

  return (
    <div
      className={styles.TimepointComponent}
      onClick={() => inputRef?.current?.focus()}
      data-test-component="TimepointComponent"
      data-test-element="container"
    >
      <div className={styles.Chips} data-test-element="chip-container">
        {Array.from(timepointValues).map((timepoint, index) => (
          <div key={`${index}_${timepoint}`} className={styles.Chip} data-test-element="chip" data-test-key={timepoint}>
            <div className={styles.Value}>
              {DateUtils.renderDuration(timepoint, { format: 'short', zeros: true, defaultResponse: '' })}
            </div>
            <a
              className={styles.Remove}
              onClick={() => {
                updateTimepointValues((prevState) => {
                  const update = new Set(prevState);
                  update.delete(timepoint);
                  handleAction({ type: 'update', value: Array.from(update), close: false });
                  return update;
                });
                inputRef?.current?.focus();
              }}
              data-test-element="delete-chip"
            >
              <RiCloseFill />
            </a>
          </div>
        ))}
      </div>
      <input className={styles.Input} ref={inputRef} type="text" data-test-element="input" />
    </div>
  );
};
